import type { FunctionComponent } from 'react';
import { useCallback, useReducer } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { intl } from '@trello/i18n';
import { showFlag } from '@trello/nachos/experimental-flags';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';

import type { MemberProfileParams } from './MemberHomeProfileTab';
import { MemberHomeProfileTab } from './MemberHomeProfileTab';
import { useMemberHomeProfileTabQuery } from './MemberHomeProfileTabQuery.generated';
import {
  initialMemberHomeProfileState,
  memberHomeProfileReducer,
  saveProfileCompleteCreator,
  saveProfileErrorCreator,
  saveProfileResetCreator,
  saveProfileStartCreator,
} from './MemberHomeProfileTabState';
import { useMemberHomeProfileTabUpdateProfileMutation } from './MemberHomeProfileTabUpdateProfileMutation.generated';

const MemberHomeProfileTabContainer: FunctionComponent = () => {
  const memberId = useMemberId();
  const { data } = useMemberHomeProfileTabQuery({
    variables: {
      memberId,
    },
    waitOn: ['MemberHeader'],
  });

  const [updateProfile] = useMemberHomeProfileTabUpdateProfileMutation();
  const [profileUIState, dispatchProfileUIState] = useReducer(
    memberHomeProfileReducer,
    initialMemberHomeProfileState,
  );

  const onSaveProfileStart = useCallback(async () => {
    dispatchProfileUIState(saveProfileStartCreator());
  }, [dispatchProfileUIState]);

  const onSaveProfileComplete = useCallback(async () => {
    showFlag({
      id: 'saveProfile',
      appearance: 'success',
      title: intl.formatMessage({
        id: 'templates.member_detail_profile_edit.saved',
        defaultMessage: 'Saved',
        description: 'Saved',
      }),
      isAutoDismiss: true,
      msTimeout: 4000,
    });
    dispatchProfileUIState(saveProfileCompleteCreator());
  }, [dispatchProfileUIState]);

  const onSubmitMemberProfile = useCallback(
    async (profile: MemberProfileParams) => {
      if (!profileUIState) {
        return;
      }
      await onSaveProfileStart();

      try {
        await updateProfile({
          variables: {
            profile: {
              bio: profile.bio,
              username: dangerouslyConvertPrivacyString(profile.username),
            },
          },
        });
        await onSaveProfileComplete();

        Analytics.sendTrackEvent({
          action: 'updated',
          actionSubject: 'member',
          source: 'profileAndVisibilitySettingsScreen',
        });
      } catch (err) {
        let errorMessage = (err as Error).message;

        // This will be removed in a future PR, originalMessage is a stringified error object
        if (errorMessage.startsWith('{')) {
          try {
            const jsonError = JSON.parse(errorMessage);
            errorMessage = jsonError.message;
          } catch (e) {
            // We don't really care it just means it's not valid JSON
          }
        }

        errorMessage = errorMessage.trim().toLowerCase();

        dispatchProfileUIState(saveProfileResetCreator());
        dispatchProfileUIState(saveProfileErrorCreator(errorMessage));
      }
    },
    [
      updateProfile,
      dispatchProfileUIState,
      onSaveProfileComplete,
      onSaveProfileStart,
      profileUIState,
    ],
  );

  const onSaveProfileError = useCallback(
    (errors: string) => {
      dispatchProfileUIState(saveProfileResetCreator());
      dispatchProfileUIState(saveProfileErrorCreator(errors));
    },
    [dispatchProfileUIState],
  );

  const { saveMessage, saveErrors } = profileUIState;

  return data?.member ? (
    <MemberHomeProfileTab
      member={data.member}
      // @ts-expect-error
      onSaveProfileError={onSaveProfileError}
      onSubmitMemberProfile={onSubmitMemberProfile}
      saveMessage={saveMessage}
      saveErrors={saveErrors}
    />
  ) : (
    <div>
      <FormattedMessage id="error" defaultMessage="Error" description="Error" />
    </div>
  );
};

export { MemberHomeProfileTabContainer };
